import { useRouter } from 'next/router'
import Link from 'next/link'

import { historyUtils } from '@wartek-id/fe-toolbox'
import {
  HeaderBar,
  HeaderBarLeftAction,
  HeaderBarRightAction,
  HeaderBarTitle,
} from '@wartek-id/header-bar'
import { Icon } from '@wartek-id/icon'
import { Shimmer } from '@wartek-id/shimmer'
import { Text } from '@wartek-id/text'

import { useAPIGetCategories } from 'api/inspirations/useAPIInspirations'
import { GeneralNoticeState } from 'components/NoticeState'

import type { FC } from 'react'

import EmptyDocumentIcon from 'assets/icons/img-empty-document.svg'

interface CategoryListProps {
  onSuccessFetching?: (slugs: Array<string>) => void
}

const CategoryList: FC<CategoryListProps> = ({ onSuccessFetching }) => {
  const router = useRouter()
  const { data, isLoading, ...api } = useAPIGetCategories('CT_ALPHABETICAL', {
    onSuccess: (res) => {
      onSuccessFetching?.(res.data?.map((category) => category.slug) || [])
    },
  })

  return (
    <>
      <PageHeader />
      <nav className="mt-[54px]">
        <ul>
          {isLoading &&
            Array.from({ length: 7 })?.map((_, index) => (
              <li className="border-b px-4 py-3" key={index}>
                <Shimmer
                  height={24}
                  noOfLines={1}
                  width={`${index % 2 === 0 ? '45' : '55'}%`}
                />
              </li>
            ))}

          {api.isError && (
            <div
              style={{
                height: 'calc(100vh - 3.5rem)',
                overflow: 'auto',
              }}
            >
              <GeneralNoticeState
                containerClassNames="px-7 pt-10 pb-7"
                showButton
                buttonLabel="Coba lagi"
                onButtonClick={() => {
                  router.reload()
                }}
              />
            </div>
          )}

          {api.isSuccess && (
            <>
              {/* If no data */}
              {data?.data?.length === 0 && (
                <div
                  style={{
                    height: 'calc(100vh - 3.5rem)',
                    overflow: 'auto',
                  }}
                >
                  <GeneralNoticeState
                    containerClassNames="px-7 pt-10 pb-7"
                    title="Tidak ada kategori"
                    description="Untuk saat ini, silakan langsung telusuri konten-konten di halaman Ide Praktik."
                    icon={<EmptyDocumentIcon />}
                    showButton
                    buttonLabel="Buka halaman Ide Praktik"
                    onButtonClick={() => {
                      router.push('/ide-praktik')
                    }}
                  />
                </div>
              )}

              {/* If has data */}
              {data?.data?.length >= 1 &&
                data?.data.map((cat, index) => {
                  const title = cat?.titleWithoutEmoji ?? cat?.title
                  if (!title) return null

                  return (
                    <li
                      key={`category-${index}`}
                      className="border-b"
                      data-testid={`category-${index}`}
                    >
                      <Link
                        href={`/ide-praktik/kategori/${cat?.slug}`}
                        passHref
                      >
                        <Text variant="body" className="px-4 py-3 block" as="a">
                          {title}
                        </Text>
                      </Link>
                    </li>
                  )
                })}
            </>
          )}
        </ul>
      </nav>
    </>
  )
}

type PageHeaderProps = {
  title?: string
  onBack?: () => void
}

const PageHeader = ({ onBack }: PageHeaderProps) => {
  const router = useRouter()

  const handleBack = () => {
    historyUtils.backToApp(router.back)
    onBack?.()
  }

  return (
    <HeaderBar fixed className="z-10">
      <HeaderBarLeftAction
        icon={
          <Icon as="i" color="default">
            arrow_back
          </Icon>
        }
        onClick={handleBack}
      />
      <HeaderBarTitle>Kategori</HeaderBarTitle>
      <HeaderBarRightAction />
    </HeaderBar>
  )
}

export default CategoryList
